import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import WebGLBackground from './components/WebGLBackground';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import Home from './pages/Home';
import CustomEA from './pages/CustomEA';
import './index.css';

import Marketplace from './pages/Marketplace';
import EADetails from './pages/EADetails';
import AdminDashboard from './pages/AdminDashboard';
import WhyUs from './pages/WhyUs';
import RequestEA from './pages/RequestEA';
import HowItWorks from './pages/HowItWorks';
import CategoryPage from './pages/CategoryPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RefundPolicy from './pages/RefundPolicy';
import Disclaimer from './pages/Disclaimer';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AccountLayout from './layouts/AccountLayout';
import DashboardOverview from './pages/account/DashboardOverview';
import MyEAs from './pages/account/MyEAs';
import MyOrders from './pages/account/MyOrders';
import MyWishlist from './pages/account/MyWishlist';
import MyRequests from './pages/account/MyRequests';
import MyCustomAlgoRequests from './pages/account/MyCustomAlgoRequests';
import Settings from './pages/account/Settings';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import PaymentStatus from './pages/PaymentStatus';
import RecentPurchasePopup from './components/RecentPurchasePopup';
import TickerTape from './components/TickerTape';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { GoogleOAuthProvider } from '@react-oauth/google';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = React.useContext(AuthContext);
  const location = useLocation();
  
  if (loading) return null;
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = React.useContext(AuthContext);
  const location = useLocation();
  
  if (loading) return null;
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (user.role !== 'admin') {
    // If logged in but not an admin, redirect to their normal dashboard
    return <Navigate to="/account/dashboard" replace />;
  }
  
  return children;
};

function App() {
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  return (
    <GoogleOAuthProvider clientId="908651262176-4nbqa085pv5osn2hlq8ph1niepesg2em.apps.googleusercontent.com">
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <Router>
              <ScrollToTop />
              <div className="App dark text-on-surface font-body-md overflow-x-hidden relative min-h-screen bg-transparent">
                <WebGLBackground />
                <Navbar />
                <TickerTape />
                
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/marketplace" element={<Marketplace />} />
                  <Route path="/ea/:id" element={<EADetails />} />
                  <Route path="/custom-ea" element={<CustomEA />} />
                  <Route path="/why-us" element={<WhyUs />} />
                  <Route path="/request" element={<RequestEA />} />
                  <Route path="/category/:platform" element={<CategoryPage />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/disclaimer" element={<Disclaimer />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                  <Route path="/payment/status/:orderId" element={<ProtectedRoute><PaymentStatus /></ProtectedRoute>} />
                  
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/account" element={<ProtectedRoute><AccountLayout /></ProtectedRoute>}>
                    <Route path="dashboard" element={<DashboardOverview />} />
                    <Route path="my-eas" element={<MyEAs />} />
                    <Route path="orders" element={<MyOrders />} />
                    <Route path="wishlist" element={<MyWishlist />} />
                    <Route path="requests" element={<MyRequests />} />
                    <Route path="custom-algo-requests" element={<MyCustomAlgoRequests />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="" element={<Navigate to="/account/dashboard" replace />} />
                  </Route>
                  <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                </Routes>
                
                <Footer />
                <WhatsAppButton />
              </div>
              <RecentPurchasePopup />
            </Router>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
